import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ICON_CONTENT_TYPES,
  PRESET_PROPERTY_ICONS,
  QK,
  UploadCancelledError,
  uploadPropertyIconPhoto,
  useSetPropertyIcon,
  type Property,
  type PropertyIcon,
} from "./api.js";
import { detectUploadMime, MAX_UPLOAD_BYTES } from "../util/imageCompression.js";
import { useBinderList } from "./binder-api.js";
import { BinderMediaThumb } from "./BinderMediaThumb.js";
import { presetIconGlyphs, presetIconLabels } from "./PropertyIconBadge.js";

/**
 * Kept as a named export for the existing call sites and tests; the logic now
 * lives with the rest of the upload rules in util/imageCompression.ts.
 */
export const detectIconContentType = detectUploadMime;

/**
 * Bottom-sheet picker for the property's display icon (same dialog pattern as
 * the delete confirm on PropertyDetail). Sources, in order: the 10 built-in
 * preset glyphs, the stored NAIP aerial view (when enrichment has produced
 * one), photos already in the property file, or a fresh upload
 * (presign → PUT → PATCH). "Reset to default" clears back to the
 * type-derived glyph. Every choice PATCHes `{ icon }` and closes on success;
 * the mutation invalidates the list + detail queries.
 *
 * Closing the dialog ABORTS any in-flight upload — a cancelled choice must
 * never be applied after the sheet is gone.
 */
export function PropertyIconPicker({
  property,
  onClose,
}: {
  property: Property;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const setIcon = useSetPropertyIcon(property.id);
  const { data: binderEntries } = useBinderList(property.id);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Presigned URLs (the aerial thumbnail) expire in ~5 minutes; a picker
  // opened from a long-idle detail page would show a dead image. Refetch the
  // detail on open so the thumbnail URL is fresh.
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: QK.detail(property.id) });
  }, [qc, property.id]);

  // Focus lands inside the dialog on open; Escape closes (and aborts).
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);
  // Abort any in-flight upload when the dialog unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  function close() {
    abortRef.current?.abort();
    onClose();
  }

  const photos = (binderEntries ?? []).flatMap((entry) =>
    (entry.media ?? []).map((media) => ({ entryId: entry.id, entryTitle: entry.title, media })),
  );
  const aerialUrl = property.insights?.aerialUrl;
  const current = property.icon;
  const busy = setIcon.isPending || uploading;

  async function choose(icon: PropertyIcon | null) {
    setError(null);
    try {
      await setIcon.mutateAsync(icon);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update the icon");
    }
  }

  async function onUpload(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setError(null);
    const contentType = detectIconContentType(file);
    if (!contentType) {
      setError("Use a JPG, PNG, WebP, or HEIC image.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("That image is too large (max 25 MB).");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const key = await uploadPropertyIconPhoto({
        propertyId: property.id,
        file,
        contentType,
        onProgress: (loaded, total) => setUploadPct(Math.round((loaded / total) * 100)),
        signal: controller.signal,
      });
      // The dialog may have closed while the bytes were in flight — a
      // cancelled upload must never become the icon.
      if (controller.signal.aborted) return;
      await setIcon.mutateAsync({ kind: "photo", value: key });
      onClose();
    } catch (err) {
      if (err instanceof UploadCancelledError) return; // user closed the sheet
      setError(err instanceof Error ? err.message : "Couldn't upload that image");
    } finally {
      abortRef.current = null;
      setUploading(false);
    }
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Change property icon"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Escape") close();
      }}
      className="fixed inset-0 z-20 flex items-end bg-slate-900/40 px-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
    >
      <div className="max-h-[85vh] w-full overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
        <h2 className="text-base font-semibold">Change icon</h2>

        {error && (
          <div role="alert" className="mt-2 rounded bg-rose-50 p-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        <p className="mt-3 text-xs uppercase tracking-wide text-slate-500">Presets</p>
        <div className="mt-1.5 grid grid-cols-5 gap-2">
          {PRESET_PROPERTY_ICONS.map((name) => {
            const active = current?.kind === "preset" && current.value === name;
            return (
              <button
                key={name}
                type="button"
                disabled={busy}
                onClick={() => void choose({ kind: "preset", value: name })}
                aria-label={presetIconLabels[name]}
                aria-pressed={active}
                className={`flex aspect-square items-center justify-center rounded-xl border text-2xl disabled:opacity-60 ${
                  active ? "border-teal-700 bg-teal-50" : "border-slate-200 bg-white"
                }`}
              >
                <span aria-hidden="true">{presetIconGlyphs[name]}</span>
              </button>
            );
          })}
        </div>

        {aerialUrl && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Aerial view</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void choose({ kind: "aerial" })}
              aria-pressed={current?.kind === "aerial"}
              className={`mt-1.5 block overflow-hidden rounded-xl border disabled:opacity-60 ${
                current?.kind === "aerial" ? "border-teal-700 ring-1 ring-teal-700" : "border-slate-200"
              }`}
            >
              <img
                src={aerialUrl}
                alt="Aerial view of this property"
                className="h-24 w-24 object-cover"
              />
            </button>
          </div>
        )}

        {photos.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Your photos</p>
            <ul className="mt-1.5 grid grid-cols-4 gap-2">
              {photos.map(({ entryId, entryTitle, media }, index) => {
                const active = current?.kind === "photo" && current.value === media.key;
                return (
                  <li key={media.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void choose({ kind: "photo", value: media.key })}
                      aria-label={`Use photo ${index + 1} (${entryTitle}) as the icon`}
                      aria-pressed={active}
                      className={`block w-full overflow-hidden rounded-md disabled:opacity-60 ${
                        active ? "ring-2 ring-teal-700" : ""
                      }`}
                    >
                      <BinderMediaThumb
                        propertyId={property.id}
                        entryId={entryId}
                        mediaId={media.id}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Upload</p>
          <label
            className={`mt-1.5 flex min-h-[44px] items-center justify-center rounded-md border border-dashed border-slate-300 bg-white px-4 text-sm font-medium ${
              busy ? "cursor-not-allowed text-slate-400" : "cursor-pointer text-teal-700"
            }`}
          >
            {uploading ? `Uploading… ${uploadPct}%` : "Upload a photo"}
            <input
              type="file"
              accept={[...ICON_CONTENT_TYPES, ".heic", ".heif"].join(",")}
              disabled={busy}
              className="sr-only"
              onChange={(e) => {
                void onUpload(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          <p className="mt-1 text-xs text-slate-500">
            JPG, PNG, WebP, or HEIC — large photos are shrunk automatically.
          </p>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={close}
            className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !current}
            onClick={() => void choose(null)}
            className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700 disabled:opacity-60"
          >
            Reset to default
          </button>
        </div>
      </div>
    </div>
  );
}
